import { graphql } from 'gatsby';
import React from 'react';

import { Box, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2'; // Grid version 2
import {
  useContentfulInspectorMode, useContentfulLiveUpdates
} from '../shared/hooks/contentful';

import { renderContentfulRichText } from '../shared/utils';
import { ContentfulAsset, ContentfulAssetImage, HomepageLink, Kicker } from './ui';

interface ProductProps {
  id: string
  image: ContentfulAsset
  heading: string
  text: string
  contentful_id: string
  links: HomepageLink[]
  aboveFold?: boolean
  richText: {
    raw: string
  }
}

function Product(props: ProductProps) {
  const inspectorProps = useContentfulInspectorMode();
  const data = useContentfulLiveUpdates({
    ...props,
    sys: { id: props.contentful_id },
  });

  return (
    <Card variant="outlined" sx={{ border: "none", background: "transparent" }}>
      <CardContent>
        <Stack
          gap={2}
          direction="column"
          justifyContent="space-between"
          alignItems="center"
        >
          <ContentfulAssetImage
            {...data.image}
            asset={data.image}
            style={{
              height: "100px",
              width: "100px",
            }}
            aboveFold={props.aboveFold}
            contentfulPreviewProps={inspectorProps({
              entryId: props.contentful_id,
              fieldId: 'image',
            })}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            {...inspectorProps({
              entryId: props.contentful_id,
              fieldId: 'heading',
            })}
            sx={{
              textAlign: !data.richText && !data.text ? "center" : "left",
            }}
          >
            {data.heading}
          </Typography>
          {data.text ? (<Typography
            variant="body1"
            {...inspectorProps({
              entryId: props.contentful_id,
              fieldId: 'text',
            })}
            sx={{
              textAlign: {
                xs: "center",
                sm: "center",
                md: "center",
                lg: "center",
              },
              whiteSpace: "pre-wrap",
            }}
          >
            {data.text}
          </Typography>) : null}

          {data.richText && data.richText.raw ? (<Typography
            variant="body1"
            {...inspectorProps({
              entryId: props.contentful_id,
              fieldId: 'richText',
            })}
            sx={{
              textAlign: {
                xs: "left",
                sm: "left",
                md: "left",
                lg: "left",
              },
              marginTop: '-20px'
            }}
          >
            {renderContentfulRichText(data.richText)}
          </Typography>
          ) : null}

          {/* <LinkList links={data.links} /> */}
        </Stack>
      </CardContent>
    </Card>
  )
}

export interface ProductListProps {
  kicker?: string
  kickerIcon?: ContentfulAsset
  heading: string
  text?: string
  content: ProductProps[]
  contentful_id: string
  aboveFold?: boolean
}

export default function ProductList(props: ProductListProps) {
  const inspectorProps = useContentfulInspectorMode();
  const data = useContentfulLiveUpdates({
    ...props,
    sys: { id: props.contentful_id },
  });

  return (
    (<Container>
      <Stack direction="column" sx={{ marginTop: 4 }} rowGap={5} {...inspectorProps({
        entryId: props.contentful_id,
        fieldId: 'content',
      })}>
        {(data.heading || data.text || data.kicker || (data.content && data.content.length > 0)
        ) && (
            <Box sx={{ textAlign: "center" }}>
              <Stack direction="column" rowGap={2}>
                {data.kicker && (
                  <div>
                    <Kicker label={data.kicker} icon={data.kickerIcon} {...inspectorProps({
                      entryId: props.contentful_id,
                      fieldId: 'kicker',
                    })} />
                  </div>
                )}

                {data.heading && (
                  <Typography variant="h1" gutterBottom {...inspectorProps({
                    entryId: props.contentful_id,
                    fieldId: 'heading',
                  })}>
                    {data.heading}
                  </Typography>
                )}

                {/* For SEO */}
                {!data.heading && (
                  <Typography variant="h1" sx={{ display: "none" }}>
                    Products
                  </Typography>
                )}

                {data.text && <Typography {...inspectorProps({
                  entryId: props.contentful_id,
                  fieldId: 'text',
                })}>{data.text}</Typography>}
              </Stack>
            </Box>
          )}
        <div>
          <Grid container spacing={{ xs: 1, sm: 1, md: 3, lg: 3 }}>
            {(data.content || []).map((product) => (
              <Grid
                key={product.id}
                size={{
                  xs: data.content.length > 3 ? 12 : 12,
                  sm: data.content.length % 2 === 0 ? 6 : 12,
                  md: data.content.length % 4 === 0 ? 3 : 4,
                }}>
                <Product {...product} aboveFold={props.aboveFold} />
              </Grid>
            ))}
          </Grid>
        </div>
      </Stack>
    </Container>)
  );
}

export const query = graphql`
  fragment HomepageProductListContent on ContentfulHomepageProductList {
    id
    kicker
    __typename
    contentful_id
    # kickerIcon {
    #   id
    #   contentful_id
    #   __typename
    #   url
    #   title
    #   gatsbyImageData(height: 20, width: 20)
    # }
    heading
    text
    content {
      id
      heading
      text
      __typename
      contentful_id
      richText {
        raw
      }
      image {
        url
        id
        contentful_id
        __typename
        title
        
        localFile {
          childImageSharp { 
          gatsbyImageData(placeholder: NONE, breakpoints: [500, 640])
          }
        }
      }
      # links {
      #   id
      #   href
      #   text
      # }
    }
  }
`
